<i18n src="@/locales/models/crop_field_certification.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/certifications/basic_certifications_producers/edit_crop_field_certification.yml"
  lang="yaml"
></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_crop_field_certification')"
    :params="{
      organization_id: organization_id,
      producer_id: producer_id,
      crop_field_certification_id: crop_field_certification_id,
    }"
  />

  <bread-crumbs :labels="[module_name, producer?.organization_producer?.producer_code]"></bread-crumbs>

  <producer-info v-if="load_general_data" :producer="producer"></producer-info>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-gray-200">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.certification_id") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="certifications.length > 1"
                  :options="certifications"
                  v-model="crop_field_certification.certification_id"
                  :class="isInvalidSelect('certification')"
                />
                <p v-else-if="certifications.length == 1">
                  {{ certifications[0].name }}
                </p>
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.crop_field_id") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="crop_fields.length > 1"
                  :options="crop_fields"
                  v-model="crop_field_certification.crop_field_id"
                  :class="isInvalidSelect('crop_field')"
                />
                <p v-else-if="crop_fields.length == 1">
                  {{ crop_fields[0].name }}
                </p>
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.certification_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="crop_field_certification.certification_code"
                  :class="[
                    'input-text',
                    isInvalid('crop_field_certification.certification_code')
                  ]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.certification_number") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="crop_field_certification.certification_number"
                  :class="['input-text', isInvalid('crop_field_certification.certification_number')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.renewal_date") }}
              </label>
              <div class="mt-1">
                <date-form v-model="crop_field_certification.renewal_date" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.expiration_date") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="crop_field_certification.expiration_date"
                  :class="isInvalid('expiration_date')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-1">
                <switch-form
                  v-model="crop_field_certification.active"
                  :label="$t('model.crop_field_certification.active')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-1">
                <switch-form
                  v-model="crop_field_certification.suspended"
                  :label="$t('model.crop_field_certification.suspended')"
                />
              </div>
            </div>
            <div
              class="sm:col-span-3"
              v-if="crop_field_certification.suspended"
            >
              <label class="label-form">
                {{ $t("model.crop_field_certification.suspended_until") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="crop_field_certification.suspended_until"
                  :class="isInvalid('suspended_until')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowProducer()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="update()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/certifications/basic_certifications_producers/manage_crop_field_certification.js";
import ProducerInfo from "@/components/views/organization/certifications/basic_certifications_producers/ProducerInfo.vue";

export default {
  name: "EditPackingHouseCertification",
  components: { ProducerInfo },
  data() {
    return {
      load_general_data: false,
      producer: {},
      crop_field_certification: {},
      proc_save_button: false,
      certifications: [],
      crop_fields: [],
    };
  },
  props: {
    organization_id: {},
    producer_id: {},
    crop_field_certification_id: {},
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
  methods: {
    loadData: async function () {
      this.fetchProducer();
      await this.fetchCertifications();
      await this.fetchCropFields();
      await this.fetchCropFieldCertification();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await Requester.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    fetchCertifications: async function () {
      let response = await Requester.fetchCertifications(
        this.organization_id,
        this.producer_id
      );

      this.certifications = response.data;
    },
    fetchCropFields: async function () {
      let response = await Requester.fetchCropFields(
        this.organization_id,
        this.producer_id
      );

      this.crop_fields = response.data;
    },
    async fetchCropFieldCertification() {
      let response = await Requester.fetchCropFieldCertification(
        this.organization_id,
        this.producer_id,
        this.crop_field_certification_id
      );
      this.crop_field_certification = response.data;
    },
    update: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.update(
          this.organization_id,
          this.producer_id,
          this.crop_field_certification_id,
          this.crop_field_certification
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowProducer() {
      this.$router.push({
        name: this.routeName("show_crop_field_certification"),
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
          crop_field_certification_id: this.crop_field_certification_id,
        },
      });
    },
  },
};
</script>
